import React, { useState, useEffect } from 'react';
import { X, Loader2, List } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { getSavedLists, createSavedList, updateSavedList } from '../lib/api';
import { toast } from 'sonner';

/**
 * Modal to save items as a new or overwrite an existing saved list.
 * Props:
 *   items: [{ product_id, qty }]
 *   onClose: () => void
 *   onSaved: () => void (optional callback)
 */
export default function SaveAsListModal({ items, onClose, onSaved }) {
  const { t } = useApp();
  const [mode, setMode] = useState('new'); // 'new' | 'overwrite'
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [existingLists, setExistingLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSavedLists()
      .then(res => { setExistingLists(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const validItems = items.filter(it => it.product_id && it.qty >= 1);
    if (validItems.length === 0) { toast.error(t('savedLists.noItems')); return; }
    setSaving(true);
    try {
      if (mode === 'new') {
        if (!name.trim()) { setSaving(false); return; }
        await createSavedList({
          name: name.trim(),
          notes: notes.trim() || null,
          items: validItems.map(it => ({ product_id: it.product_id, qty: it.qty })),
        });
        toast.success(t('savedLists.saved'));
      } else {
        if (!selectedListId) { setSaving(false); return; }
        await updateSavedList(selectedListId, {
          items: validItems.map(it => ({ product_id: it.product_id, qty: it.qty })),
        });
        toast.success(t('savedLists.updated'));
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('savedLists.failedSave'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="save-list-modal-overlay">
      <div className="bg-[#111] border border-white/10 w-full max-w-md mx-4" data-testid="save-list-modal">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <List className="w-5 h-5 text-[#00D4FF]" />
            {t('savedLists.saveFromCart')}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-save-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-white/50 text-sm">{items.length} {t('savedLists.itemCount')}</p>

          {/* Mode selector */}
          <div className="flex gap-3">
            <button onClick={() => setMode('new')} className={`flex-1 p-3 border text-sm font-medium transition-colors ${mode === 'new' ? 'border-[#00D4FF] text-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 text-white/50'}`} data-testid="mode-new-btn">
              {t('savedLists.createNewList')}
            </button>
            <button onClick={() => setMode('overwrite')} disabled={existingLists.length === 0} className={`flex-1 p-3 border text-sm font-medium transition-colors disabled:opacity-30 ${mode === 'overwrite' ? 'border-[#00D4FF] text-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 text-white/50'}`} data-testid="mode-overwrite-btn">
              {t('savedLists.overwriteExisting')}
            </button>
          </div>

          {mode === 'new' ? (
            <>
              <div>
                <label className="block text-sm text-white/60 mb-1">{t('savedLists.name')}</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder={t('savedLists.namePlaceholder')} className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm outline-none" data-testid="new-list-name" />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">{t('savedLists.notes')}</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('savedLists.notesPlaceholder')} rows={2} className="w-full px-4 py-2 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm outline-none" data-testid="new-list-notes" />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm text-white/60 mb-1">{t('savedLists.selectList')}</label>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#00D4FF]" />
              ) : (
                <select value={selectedListId} onChange={e => setSelectedListId(e.target.value)} className="w-full px-4 py-2.5 bg-black border border-white/10 text-white text-sm" data-testid="overwrite-list-select">
                  <option value="">--</option>
                  {existingLists.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.item_count} {t('savedLists.itemCount')})</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <button onClick={handleSave} disabled={saving || (mode === 'new' && !name.trim()) || (mode === 'overwrite' && !selectedListId)} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40" data-testid="save-list-confirm-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <List className="w-4 h-4" />}
            {saving ? t('savedLists.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
